// AdminDashboard.js 
import React, { useState, useCallback, useMemo, lazy, Suspense } from 'react';
import { useAuth } from '../context/AuthContext'; // Now using AuthContext
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import '../styles/AdminDashboard.css'; 

// Create a separate context or a simple state management solution
// to avoid circular dependencies
const DashboardState = React.createContext(null);

// Use the Provider Pattern - simple context with minimal props
function DashboardProvider({ children }) {
  const [selectedCatalogItem, setSelectedCatalogItem] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);

  const value = {
    selectedCatalogItem,
    setSelectedCatalogItem,
    selectedLesson,
    setSelectedLesson
  };
  
  return (
    <DashboardState.Provider value={value}>
      {children}
    </DashboardState.Provider>
  );
}

// Loading component for Suspense fallback
const LoadingComponent = () => (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <p>Loading component...</p>
  </div>
);

// Lazy load all components to break dependencies
const MediaPage = lazy(() => import('../features/MediaLibrary/MediaPage'));
const CatalogsPage = lazy(() => import('../features/catalogs/CatalogPage'));
const LessonsManager = lazy(() => import('../features/lessons/LessonsManager'));
const UnitsManager = lazy(() => import('../features/units/UnitsManager'));
const UsersPage = lazy(() => import('../features/users/UsersPage'));
const GroupsPage = lazy(() => import('../features/groups/GroupsPage'));
const SessionsPage = lazy(() => import('../features/sessions/SessionsPage'));
const LearnerImpactPage = lazy(() => import('../features/learnerImpact/LearnerImpactPage'));
const FeedbackPage = lazy(() => import('../features/surveys/FeedbackPage'));

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('catalogs');
  const { logout, currentUser, userRole, userData } = useAuth(); // Now using AuthContext
  const navigate = useNavigate();
  
  // Get state from context
  const dashboardState = React.useContext(DashboardState);
  const {
    selectedCatalogItem,
    setSelectedCatalogItem,
    selectedLesson,
    setSelectedLesson
  } = dashboardState;
  
  // Memoize logout handler using AuthContext
  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [logout, navigate]);
  
  // Memoize catalog selection handler
  const handleSelectCatalog = useCallback((catalog) => {
    setSelectedCatalogItem(catalog);
  }, [setSelectedCatalogItem]);

  // Memoize lesson selection handler
  const handleSelectLesson = useCallback((lesson) => {
    setSelectedLesson(lesson);
  }, [setSelectedLesson]);

  // Back from lessons view to the catalog list
  const handleBackToCatalogs = useCallback(() => {
    setSelectedCatalogItem(null);
    setSelectedLesson(null);
  }, [setSelectedCatalogItem, setSelectedLesson]);

  // Back from units view to the lessons list (stay within the same catalog)
  const handleBackToLessons = useCallback(() => {
    setSelectedLesson(null);
  }, [setSelectedLesson]);
  
  // Memoize tab change handler
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);
  
  // Memoize the tab content rendering to prevent unnecessary re-renders
  const tabContent = useMemo(() => {
    // Wrap components with Suspense to handle lazy loading
    switch(activeTab) {
      case 'catalogs':
        // Drill-down level 2: a lesson is selected -> show its units/content
        if (selectedLesson) {
          return (
            <Suspense fallback={<LoadingComponent />}>
              <button onClick={handleBackToLessons} style={{ marginBottom: '1rem' }}>
                ← Back to Lessons
              </button>
              <UnitsManager db={db} selectedLesson={selectedLesson} />
            </Suspense>
          );
        }
        // Drill-down level 1: a catalog is selected -> show its lessons
        if (selectedCatalogItem) {
          return (
            <Suspense fallback={<LoadingComponent />}>
              <button onClick={handleBackToCatalogs} style={{ marginBottom: '1rem' }}>
                ← Back to Catalogs
              </button>
              <LessonsManager
                db={db}
                selectedCatalogItem={selectedCatalogItem}
                onSelectLesson={handleSelectLesson}
              />
            </Suspense>
          );
        }
        // Top level: catalog list
        return (
          <Suspense fallback={<LoadingComponent />}>
            <CatalogsPage onSelectCatalog={handleSelectCatalog} />
          </Suspense>
        );
      case 'media':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <MediaPage />
          </Suspense>
        );
      case 'users':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <UsersPage db={db} />
          </Suspense>
        );
      case 'groups':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <GroupsPage />
          </Suspense>
        );
      case 'sessions':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <SessionsPage />
          </Suspense>
        );
      case 'learnerImpact':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <LearnerImpactPage />
          </Suspense>
        );
      case 'feedback':
        return (
          <Suspense fallback={<LoadingComponent />}>
            <FeedbackPage />
          </Suspense>
        );
      default:
        return <div>Select a tab</div>;
    }
  }, [
    activeTab,
    selectedCatalogItem,
    selectedLesson,
    handleSelectCatalog,
    handleSelectLesson,
    handleBackToCatalogs,
    handleBackToLessons
  ]);
  
  // Memoize tab buttons to prevent unnecessary re-renders
  const tabButtons = useMemo(() => (
    <div className="tabs">
      <button 
        className={activeTab === 'catalogs' ? 'active' : ''} 
        onClick={() => handleTabChange('catalogs')}
      >
        Catalogs
      </button>
      <button 
        className={activeTab === 'media' ? 'active' : ''} 
        onClick={() => handleTabChange('media')}
      >
        Media Library
      </button>
      <button 
        className={activeTab === 'users' ? 'active' : ''} 
        onClick={() => handleTabChange('users')}
      >
        Users
      </button>
      <button 
        className={activeTab === 'groups' ? 'active' : ''} 
        onClick={() => handleTabChange('groups')}
      >
        Groups
      </button>
      <button 
        className={activeTab === 'sessions' ? 'active' : ''} 
        onClick={() => handleTabChange('sessions')}
      >
        Sessions
      </button>
      <button 
        className={activeTab === 'learnerImpact' ? 'active' : ''} 
        onClick={() => handleTabChange('learnerImpact')}
      >
        Learner Impact
      </button>
      <button 
        className={activeTab === 'feedback' ? 'active' : ''} 
        onClick={() => handleTabChange('feedback')}
      >
        Feedback
      </button>
    </div>
  ), [activeTab, handleTabChange]);
  
  return (
    <div className="admin-dashboard">
      <header>
        <div className="header-content">
          <h1>LMS Content Management System</h1>
          <div className="user-controls">
            {currentUser && (
              <>
                <span className="user-email">{currentUser.email}</span>
                <button 
                  className="logout-button"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {userRole === 'official' ? (
        <div className="tab-content">
          <Suspense fallback={<LoadingComponent />}>
            <LearnerImpactPage lockedCohortId={userData?.cohortId} />
          </Suspense>
        </div>
      ) : (
        <>
          {tabButtons}
          <div className="tab-content">
            {tabContent}
          </div>
        </>
      )}
    </div>
  );
}

// Wrap AdminDashboard with the provider to avoid circular dependencies
function AdminDashboardWithProvider() {
  return (
    <DashboardProvider>
      <AdminDashboard />
    </DashboardProvider>
  );
}

export default AdminDashboardWithProvider;